import * as React from 'react';
import bindAllOfThis from "src/utils/BindThisHelper"; // 'this' binder
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';

import Button from 'antd/lib/button/button';
import { message, Form, Input, Row, Col, Select, AutoComplete } from 'antd';
import UniversalUserSearchDTO from 'src/models/user/UniversalUserSearchDTO';
import { NameOf } from 'src/utils/NameOf';
import { FormComponentProps } from 'antd/lib/form';
import { Roles } from 'src/constants/Roles';
import KeyValueDTO from 'src/models/generic/KeyValueDTO';
import { Grades } from 'src/constants/Grades';
import { withRouter, RouteComponentProps } from 'react-router';
import ArrayUtil from 'src/utils/ArrayUtil';
import FieldDecorator from 'src/components/common/form/FieldDecorator';
import DebugUtil from 'src/utils/DebugUtil';

export interface UnitSearchProps {
    firstName?: string;
    lastName?: string;
    email?: string;
    stateName?: string;
    districtName?: string;
    schoolName?: string;
    clientName?: string;
    status?: boolean;
    studentId?: string;
    roleId?: number;
    gradeId?: number;
}

interface UniversalUserSearchFormProps extends FormComponentProps, LocalizeContextProps, RouteComponentProps<{}> {
    isLoading: boolean;
    /** Indicates that the filters are reloading */
    isFilterLoading: boolean;
    states: KeyValueDTO<number, string>[];
    districts: KeyValueDTO<number, string>[];
    schools: KeyValueDTO<number, string>[];
    clients: KeyValueDTO<number, string>[];

    onSubmit: (values: UnitSearchProps) => void;
    onReset: () => void;
    /** Called when the state filter is changed */
    onStateFilter?: (stateId: string) => void;
    /** Called when the district filter is changed */
    onDistrictFilter?: (stateId: string) => void;
}

interface UniversalUserSearchFormState {
    /** This exists because the reset button will fire off onSubmit(), causing validation errors to be shown to the user */
    isResetting: boolean;
    filter: UnitSearchProps;
    filteredSchools: string[];
    filteredClients: string[];
}

class UniversalUserSearchForm extends React.Component<UniversalUserSearchFormProps, UniversalUserSearchFormState> {
    constructor(props: UniversalUserSearchFormProps) {
        super(props);
        bindAllOfThis(this, UniversalUserSearchForm.prototype);

        this.state = {
            filter: {},
            isResetting: false,
            filteredSchools: [],
            filteredClients: []
        };
    }

    componentDidMount() {
        // Check if state exists so we can just repopulate it for the user
        let locationState = this.props.location.state as UnitSearchProps;
        if (locationState != null) {
            this.props.form.setFieldsValue(locationState);
            if (this.props.onSubmit != null) {
                this.props.onSubmit(locationState);
            }
        }

        // Clear state on reload
        this.props.history.replace({ ...this.props.history.location, state: undefined });
    }

    private handleFilterSchools(value: string) {
        let input = (value || "").toLowerCase();

        // Filter the schools if input is 3+ chars OR if the number of schools is small, like 50 or so
        if (input.length > 2 || this.props.schools.length < 50) {
            let filteredSchools = this.props.schools
                .filter(x => x.value.toLowerCase().indexOf(input) >= 0)
                .map(x => x.value);
            this.setState({ filteredSchools: ArrayUtil.Unique(filteredSchools) });
            return;
        }

        this.setState({ filteredSchools: [] });
    }

    private handleFilterClients(value: string) {
        let input = (value || "").toLowerCase();

        // Filter the clients if input is 3+ chars OR if the number of schools is small, like 50 or so
        if (input.length > 2 || this.props.clients.length < 50) {
            let filteredClients = this.props.clients
                .filter(x => x.value.toLowerCase().indexOf(input) >= 0)
                .map(x => x.value);
            this.setState({ filteredClients: ArrayUtil.Unique(filteredClients) });
            return;
        }

        this.setState({ filteredClients: [] });
    }

    private handleReset() {
        this.setState({
            isResetting: true
        }, () => this.props.form.resetFields());
    }

    private onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        // Don't validate on reset
        if (this.state.isResetting) {
            this.setState({ isResetting: false });
            if (this.props.onReset != null) {
                this.props.onReset();
            }
            return;
        }

        // Make sure all fields are valid
        this.props.form.validateFields((errors, values) => {
            /** Business logic time! **/

            // Ensure that atleast one of the filter has been filled in
            var filters = Object.keys(values).map(key => ({ key: key, value: values[key] as string }));
            var filtersInUse = filters.filter(x => x.value != null && x.value.toString().length > 0);
            if (filtersInUse.length === 0) {
                // Oh no!
                message.error("You must select at least one filter below to search");
                return;
            }

            // There are a few filters that require a second filter to be used
            if (filtersInUse.length === 1) {
                let filter = filtersInUse[0];
                let restrictedFilters = ["Status", "studentId", "gradeId", "stateId", "districtName", "clientName"];

                if (restrictedFilters.indexOf(filter.key) > 0) {
                    message.error("You must select a second filter as the one you selected is too general");
                    return;
                }
            }

            // Reduce 'values' to just the values that have values
            var reducedValues = {};
            Object.keys(values).forEach(x => {
                let newVal = values[x];
                if (newVal != null && newVal.toString().length > 0) {
                    reducedValues[x] = newVal;
                }
            });

            if (!errors) {
                if (this.props.onSubmit != null) {
                    this.props.onSubmit(reducedValues);
                }
            }
        });
    }

    render() {
        const formItemLayout = {
            labelCol: { xs: { span: 12 }, lg: { span: 10 } },
            wrapperCol: { xs: { span: 12 }, lg: { span: 14 } },
        };

        return <Form layout="horizontal" onSubmit={this.onSubmit}>
            <Row type="flex" align="middle" justify="center">
                <Col span={8} pull={1}>
                    <Row>
                        <Col span={24}>
                            <Form.Item label={"Role"} {...formItemLayout}>
                                {
                                    this.props.form.getFieldDecorator(NameOf<UniversalUserSearchDTO>("roleId"))
                                        (<Select
                                            filterOption={(input, option) => option.props.title.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                            allowClear
                                            showSearch
                                            mode="default">
                                            {
                                                Roles.All.map(x => <Select.Option key={x.id} value={x.id} title={x.display}>{x.display}</Select.Option>)
                                            }
                                        </Select>)
                                }
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label={"First Name"} {...formItemLayout}>
                                {this.props.form.getFieldDecorator(NameOf<UniversalUserSearchDTO>("firstName"), {
                                    rules: [{ min: 2, message: "Please enter 2 or more characters" }]
                                })(<Input autoComplete="off" />)}
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label={"Last Name"} {...formItemLayout}>
                                {this.props.form.getFieldDecorator(NameOf<UniversalUserSearchDTO>("lastName"), {
                                    rules: [{ min: 2, message: "Please enter 2 or more characters" }]
                                })(<Input autoComplete="off" />)}
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label={"Email"} {...formItemLayout}>
                                {this.props.form.getFieldDecorator(NameOf<UniversalUserSearchDTO>("email"), {
                                    rules: [{ min: 2, message: "Please enter 2 or more characters" }]
                                })(<Input autoComplete="off" />)}
                            </Form.Item>
                        </Col>
                    </Row>
                </Col>
                <Col span={8} pull={1}>
                    <Row>
                        <Col span={24}>
                            <Form.Item label={"State Name"} {...formItemLayout}>
                                {
                                    this.props.form.getFieldDecorator(NameOf<UniversalUserSearchDTO>("stateId"))
                                        (<Select
                                            filterOption={(input, option) => option.props.title.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                            onChange={(value: string) => this.props.onStateFilter(value)}
                                            allowClear
                                            showSearch
                                            mode="default">
                                            {
                                                this.props.states.map(x => <Select.Option key={x.key} value={x.key} title={x.value}>{x.value}</Select.Option>)
                                            }
                                        </Select>)
                                }
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label={"District Name"} {...formItemLayout}>
                                {
                                    this.props.form.getFieldDecorator(NameOf<UniversalUserSearchDTO>("districtId"))
                                        (<Select
                                            filterOption={(input, option) => option.props.title.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                            onChange={(value: string) => this.props.onDistrictFilter(value)}
                                            allowClear
                                            showSearch
                                            mode="default">
                                            {
                                                this.props.districts.map(x => <Select.Option key={x.key} value={x.key} title={x.value}>{x.value}</Select.Option>)
                                            }
                                        </Select>)
                                }
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label={"School Name"} {...formItemLayout}>
                                {this.props.form.getFieldDecorator(NameOf<UniversalUserSearchDTO>("schoolName"))
                                    (<AutoComplete
                                        allowClear
                                        onChange={this.handleFilterSchools}
                                        {...{ onFocus: this.handleFilterSchools }} // Hacky because antd types are old, but it is there
                                    >{this.state.filteredSchools.map(x => <AutoComplete.Option key={x}>{x}</AutoComplete.Option>)}</AutoComplete>)}
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label={"Client Name"} {...formItemLayout}>
                                {this.props.form.getFieldDecorator(NameOf<UniversalUserSearchDTO>("clientName"))
                                    (<AutoComplete
                                        allowClear
                                        onChange={this.handleFilterClients}
                                        {...{ onFocus: this.handleFilterClients }} // Hacky because antd types are old, but it is there
                                    >{this.state.filteredClients.map(x => <AutoComplete.Option key={x}>{x}</AutoComplete.Option>)}</AutoComplete>)}
                            </Form.Item>
                        </Col>
                    </Row>
                </Col>
                <Col span={8} pull={1}>
                    <Row>
                        <Col span={24}>
                            <Form.Item label={"Student ID"} {...formItemLayout}>
                                {this.props.form.getFieldDecorator(NameOf<UniversalUserSearchDTO>("studentId"), {
                                    rules: [{ min: 2, message: "Please enter 2 or more characters" }]
                                })(<Input autoComplete="off" />)}
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label={"Grade"} {...formItemLayout}>
                                {this.props.form.getFieldDecorator(NameOf<UniversalUserSearchDTO>("gradeId"))
                                (<Select
                                    filterOption={(input, option) => option.props.title.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                    allowClear
                                    showSearch
                                    mode="default">
                                    {
                                        Grades.All.map(x => <Select.Option key={x.id} value={x.id} title={x.shortName}>{x.shortName}</Select.Option>)
                                    }
                                </Select>)}
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label={"Status"} {...formItemLayout}>
                                {this.props.form.getFieldDecorator(NameOf<UniversalUserSearchDTO>("status"))
                                (<Select
                                    // filterOption={(input, option) => option.props.title.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                    allowClear
                                    // showSearch
                                    mode="default">
                                        <Select.Option key={"status-active"} value={"true"} title={"Active"}>Active</Select.Option>
                                        <Select.Option key={"status-inactive"} value={"false"} title={"Inactive"}>Inactive</Select.Option>
                                </Select>)}
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <FieldDecorator
                                form={this.props.form}
                                label={"Is Clever"}
                                name={NameOf<UniversalUserSearchDTO>("isClever")}
                                visible={DebugUtil.isDebugEnabled()}
                                {...formItemLayout}
                            >
                                <Select allowClear mode="default">
                                    <Select.Option key={"clever-yes"} value={"true"}>Yes</Select.Option>
                                    <Select.Option key={"clever-no"} value={"false"}>No</Select.Option>
                                </Select>
                            </FieldDecorator>
                        </Col>
                        <Col span={24}>
                            <FieldDecorator
                                form={this.props.form}
                                label={"Is ClassLink"}
                                name={NameOf<UniversalUserSearchDTO>("isClassLink")}
                                visible={DebugUtil.isDebugEnabled()}
                                {...formItemLayout}
                            >
                                <Select allowClear mode="default">
                                    <Select.Option key={"classlink-yes"} value={"true"}>Yes</Select.Option>
                                    <Select.Option key={"classlink-no"} value={"false"}>No</Select.Option>
                                </Select>
                            </FieldDecorator>
                        </Col>
                        <Col span={24}>
                            <Row type="flex" justify="end">
                                <Col>
                                    <Form.Item>
                                        <Button type="primary" icon="download" loading={this.props.isLoading || this.props.isFilterLoading} htmlType="submit">Search</Button>
                                        &nbsp;
                                        <Button type="default" onClick={this.handleReset} htmlType="submit">Clear</Button>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Form>;
    }
}

export default withRouter(withLocalize(Form.create()(UniversalUserSearchForm)));



// WEBPACK FOOTER //
// ./src/pages/admin/UniversalUserSearchForm.tsx